<script setup lang="ts">
import { computed, onMounted, onScopeDispose, ref } from 'vue';

import { getIndexStatus } from '@/api/apiClient/indexStatus';
import { refreshIndex } from '@/api/apiClient/settings';
import { getUnits } from '@/api/apiClient/units';
import type { IndexStatus, UnitSummary } from '@/api/types';
import { useConfigStore } from '@/features/config/configStore';
import SetupNotice from '@/features/config/SetupNotice.vue';
import { externalHref } from '@/utils/externalHref';

// The cadence at which the "indexing…" panel polls for completion, injectable so
// a test can drive the recovery loop without waiting on the wall clock.
const props = withDefaults(defineProps<{ pollIntervalMs?: number }>(), {
  pollIntervalMs: 2000,
});

const config = useConfigStore();
const units = ref<UnitSummary[]>([]);
const loaded = ref(false);
const failed = ref(false);
const refreshing = ref(false);
// Whether the registrar is building its index right now. While true the page
// shows a live progress panel and keeps polling for completion instead of
// latching the "could not load" error — the union fetch 503s until the first
// read lands (APPLICATION_SPEC item 2: "show the progress of the index").
const indexing = ref(false);

// Recursive-setTimeout poll state. `stopped` is re-checked after every await so
// a tick that resolves after the component unmounts (or after the index is
// ready) bails before touching state or scheduling another tick.
let pollTimer: ReturnType<typeof setTimeout> | undefined;
let stopped = false;

async function fetchUnits(): Promise<void> {
  units.value = (await getUnits()).units;
}

function stopPolling(): void {
  stopped = true;
  clearTimeout(pollTimer);
  pollTimer = undefined;
}

// One poll tick. Reads the index status and either finishes (ready → load the
// units and clear the panel), keeps waiting (still indexing → re-arm), or gives
// up (idle-and-not-ready, or the status itself is unreachable → fall back to the
// recoverable error / setup notice). The next tick is scheduled only after this
// one resolves, so ticks never overlap. `ready` never regresses once set and
// `/units` answers whenever it is true, so a ready status always loads.
async function pollIndexStatus(): Promise<void> {
  if (stopped) {
    return;
  }
  let status: IndexStatus;
  try {
    status = await getIndexStatus();
  } catch {
    stopPolling();
    indexing.value = false;
    failed.value = true;
    return;
  }
  if (stopped) {
    return;
  }
  if (status.ready) {
    stopPolling();
    try {
      await fetchUnits();
      failed.value = false;
    } catch {
      failed.value = true;
    } finally {
      indexing.value = false;
    }
    return;
  }
  if (status.indexing) {
    indexing.value = true;
    failed.value = false;
    pollTimer = setTimeout(() => void pollIndexStatus(), props.pollIntervalMs);
    return;
  }
  stopPolling();
  indexing.value = false;
  failed.value = true;
}

async function load(): Promise<void> {
  try {
    await fetchUnits();
    failed.value = false;
    indexing.value = false;
  } catch {
    // The union fetch failed — most often because the index has not been read
    // yet. Consult the index status: show live progress while it builds, else
    // fall through to the recoverable error / setup notice.
    stopped = false;
    await pollIndexStatus();
  } finally {
    loaded.value = true;
  }
}

onMounted(load);
onScopeDispose(stopPolling);

// Right after the operator connects a token and creates the repo, the index is
// briefly not-ready and the union fetch fails, latching the error panel. This
// re-polls the index (a full server-side refresh) and re-fetches, clearing the
// error on success and keeping the panel for another try on failure — so the
// operator recovers without a full page reload.
async function onRefresh(): Promise<void> {
  refreshing.value = true;
  try {
    await refreshIndex();
    await fetchUnits();
    failed.value = false;
  } catch {
    failed.value = true;
  } finally {
    refreshing.value = false;
  }
}

const published = computed(() => units.value.filter((u) => u.state === 'published'));
const inReview = computed(() => units.value.filter((u) => u.state === 'in_review'));

const skillCount = computed(() => units.value.filter((u) => u.type === 'skill').length);
const skillsetCount = computed(() => units.value.filter((u) => u.type === 'skillset').length);
const subagentCount = computed(() => units.value.filter((u) => u.type === 'subagent').length);

// The server omits the downloads field entirely when download tracking is off;
// mirror its home page and hide the tile rather than showing a misleading "0".
const downloadsTracked = computed(() => units.value.some((u) => u.downloads !== undefined));
const downloadTotal = computed(() =>
  units.value.reduce((sum, u) => sum + (u.downloads ?? 0), 0),
);

// Prefer published units; when none are published yet, fall back to a sample of
// the (non-in-review) catalog so the grid is not empty — mirroring the server.
const discoverable = computed(() =>
  published.value.length ? published.value : units.value.filter((u) => u.state !== 'in_review'),
);
const discovery = computed(() =>
  [...discoverable.value]
    .sort((a, b) => (b.downloads ?? 0) - (a.downloads ?? 0) || a.name.localeCompare(b.name))
    .slice(0, 6),
);
const discoveryHeading = computed(() =>
  downloadsTracked.value ? 'Most downloaded' : 'In the registry',
);

// The catalog is derived from Git, which carries no creation time, so recency
// is a fact the server records the first time it observes each unit and reports
// as `firstSeen`. Show the newest first, excluding in-review units (they have
// their own section and are not yet installable), and hide the whole section
// when the deployment tracks no first-seen times.
const recentlyAdded = computed(() =>
  units.value
    .filter((u) => u.firstSeen !== undefined && u.state !== 'in_review')
    .sort(
      (a, b) => (b.firstSeen ?? 0) - (a.firstSeen ?? 0) || a.name.localeCompare(b.name),
    )
    .slice(0, 6),
);

const isEmpty = computed(() => loaded.value && units.value.length === 0);

function unitLink(u: UnitSummary): string {
  return `/units/${u.path}`;
}
</script>

<template>
  <div class="home">
    <section class="hero">
      <h1>Nori Skillsets</h1>
      <p class="text-muted">
        Browse, build, and publish skillsets, skills, and subagents.
      </p>
      <div class="hero-actions">
        <RouterLink class="btn btn-primary" to="/units">Browse registry</RouterLink>
        <RouterLink class="btn" to="/new">New unit</RouterLink>
      </div>
    </section>

    <SetupNotice v-if="failed && config.needsSetup" />

    <section
      v-else-if="indexing"
      class="indexing"
      data-testid="indexing"
      role="status"
      aria-live="polite"
    >
      <h2>Indexing the registry…</h2>
      <p class="text-muted">
        Reading the repository. This page will update automatically when it's ready.
      </p>
    </section>

    <section v-else-if="failed" class="load-error" data-testid="load-error">
      <div role="alert">
        <h2>Couldn't load the registry</h2>
        <p class="text-muted">
          The registry index could not be reached. It may still be starting up.
        </p>
      </div>
      <button
        class="btn btn-primary"
        type="button"
        data-testid="home-refresh"
        :disabled="refreshing"
        @click="onRefresh"
      >
        {{ refreshing ? 'Refreshing…' : 'Refresh' }}
      </button>
    </section>

    <section v-else-if="isEmpty" class="empty-state" data-testid="empty-state">
      <h2>Nothing here yet</h2>
      <p class="text-muted">Add the first unit to the registry.</p>
      <RouterLink class="btn btn-primary" to="/new">Create a unit</RouterLink>
    </section>

    <template v-else>
      <section class="stats" aria-label="Registry statistics">
        <div class="stat">
          <span class="stat-value" data-testid="stat-skills">{{ skillCount }}</span>
          <span class="stat-label text-muted text-sm">Skills</span>
        </div>
        <div class="stat">
          <span class="stat-value" data-testid="stat-skillsets">{{ skillsetCount }}</span>
          <span class="stat-label text-muted text-sm">Skillsets</span>
        </div>
        <div class="stat">
          <span class="stat-value" data-testid="stat-subagents">{{ subagentCount }}</span>
          <span class="stat-label text-muted text-sm">Subagents</span>
        </div>
        <div v-if="downloadsTracked" class="stat">
          <span class="stat-value" data-testid="stat-downloads">{{ downloadTotal }}</span>
          <span class="stat-label text-muted text-sm">Downloads</span>
        </div>
      </section>

      <section class="discovery" data-testid="discovery">
        <h2>{{ discoveryHeading }}</h2>
        <ul class="unit-grid">
          <li v-for="u in discovery" :key="u.path" class="unit-card">
            <RouterLink :to="unitLink(u)">
              <span class="unit-name" data-testid="unit-name">{{ u.name }}</span>
              <span class="unit-type text-muted text-sm">{{ u.type }}</span>
              <span v-if="u.description" class="unit-desc text-muted text-sm">
                {{ u.description }}
              </span>
            </RouterLink>
          </li>
        </ul>
      </section>

      <section
        v-if="recentlyAdded.length"
        class="recently-added"
        data-testid="recently-added"
      >
        <h2>Recently added</h2>
        <ul class="unit-grid">
          <li v-for="u in recentlyAdded" :key="u.path" class="unit-card">
            <RouterLink :to="unitLink(u)">
              <span class="unit-name" data-testid="unit-name">{{ u.name }}</span>
              <span class="unit-type text-muted text-sm">{{ u.type }}</span>
              <span v-if="u.description" class="unit-desc text-muted text-sm">
                {{ u.description }}
              </span>
            </RouterLink>
          </li>
        </ul>
      </section>

      <section v-if="inReview.length" class="in-review" data-testid="in-review">
        <h2>In review</h2>
        <ul class="review-list">
          <li v-for="u in inReview" :key="u.path" class="review-item">
            <span class="unit-name">{{ u.name }}</span>
            <a :href="externalHref(u.review?.url)" target="_blank" rel="noopener noreferrer">
              PR #{{ u.review?.number }}
            </a>
          </li>
        </ul>
      </section>
    </template>
  </div>
</template>

<style scoped>
.home {
  max-width: 1200px;
  margin: 0 auto;
  padding: 3rem 2rem;
}

.hero {
  text-align: center;
  padding: 2rem 0 3rem;
}

.hero h1 {
  font-family: var(--font-display);
  font-size: 2.5rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  margin: 0 0 0.5rem;
}

.hero-actions {
  display: flex;
  gap: 0.75rem;
  justify-content: center;
  margin-top: 1.5rem;
}

.stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 1rem;
  margin-bottom: 3rem;
}

.stat {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 1.25rem;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--background);
}

.stat-value {
  font-size: 1.75rem;
  font-weight: 600;
}

.unit-grid {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 1rem;
}

.unit-card {
  border: 1px solid var(--border);
  border-radius: 8px;
  transition:
    border-color 0.15s,
    box-shadow 0.15s;
}

.unit-card:hover {
  border-color: var(--border-hover);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
}

.unit-card a {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 1rem;
}

.unit-name {
  font-weight: 600;
}

.discovery,
.recently-added,
.in-review {
  margin-bottom: 3rem;
}

.discovery h2,
.recently-added h2,
.in-review h2 {
  font-size: 1.25rem;
  margin-bottom: 1rem;
}

.review-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.review-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.6rem 0;
  border-bottom: 1px solid var(--border);
}

.load-error,
.empty-state,
.indexing {
  text-align: center;
  padding: 3rem 0;
}

.indexing h2 {
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
}

.load-error .btn,
.empty-state .btn {
  margin-top: 1rem;
}
</style>
